import {
  WSActions,
  WSCard,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSectionToolbar,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { useWSMutation } from "@ws-react-query";
import { useMemo } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { queriesInternalAccounts, queriesPayouts } from "@query";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { internalAccountsApi, payoutsApi } from "@api";
import {
  Currency,
  InternalAccount,
  InternalAccountType,
  PayoutDestinationType,
  PreferredPayoutSpeed,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { dictionaryInternalAccountCurrency } from "../../../shared/constants/currency";
import { getCurrencyOption } from "../../../shared/utils/currency";

export type FormData = {
  currency: `${Currency}` | null;
};

type Props = {
  onBack?: () => void;
  onContinue?: () => void;
};

export const FormAddPlatformBalance: React.FC<Props> = props => {
  const availableCurrencies = useMemo(() => {
    return dictionaryInternalAccountCurrency["ApPayouts"] || [];
  }, []);

  const [submit, meta] = useWSMutation<
    InternalAccount,
    ResponseError,
    FormData
  >(
    async data => {
      if (!data.currency) {
        throw new Error("Currency is required");
      }

      const internalAccount = await internalAccountsApi.createInternalAccount({
        internalAccountCreate: {
          type: InternalAccountType.AP_PAYOUTS,
          currency: data.currency as Currency
        }
      });

      const payoutSettingsList =
        await queriesPayouts.listPayoutSettings.fetchQueryData();
      const payoutSettingsId =
        selectorOwnPayoutSettings(payoutSettingsList)?.id;

      if (payoutSettingsId) {
        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
            standardDestination: {
              id: internalAccount.id,
              type: PayoutDestinationType.INTERNAL_ACCOUNT
            }
          }
        });
      }

      return internalAccount;
    },
    {
      dependencies: [
        queriesInternalAccounts.listInternalAccounts.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ],
      onSuccess: props.onContinue
    }
  );

  return (
    <WSForm<FormData>
      validationSchema={Yup.object().shape({
        currency: Yup.string().nullable().required("Required")
      })}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSCard>
          <WSList gap="2XL">
            <WSElement>
              <WSSectionToolbar title="Introducing Platform Balance" />

              <WSInfoBox title="Platform balance">
                <p>
                  You have a Platform Balance available to you to hold your
                  earnings securely in a preferred currency of your choice until
                  you are ready to transfer it out to the bank account you’ve
                  added. This is a benefit available to you for free with the
                  Wingspan platform.
                </p>

                <p>
                  <b>Why is this offered?</b> Some non-U.S. contractors prefer
                  to keep their earnings on the platform in a preferred currency
                  until they are ready to transfer the balance to their external
                  bank account for various reasons (such as exchange rates and
                  inflation). Additionally, in the event that a payout to the
                  external bank account fails repeatedly, the earnings will be
                  held in the Platform Balance - allowing contractors to
                  transfer it out once any issues are resolved.
                </p>
              </WSInfoBox>
            </WSElement>

            <WSForm.Field
              name="currency"
              component={WSSelect}
              componentProps={{
                label: "Platform Balance currency",
                helperText:
                  "Please select a supported currency. Once set, this cannot be changed.",
                options: availableCurrencies.map(getCurrencyOption)
              }}
            />

            <WSErrorMessage contextKey="InternalAccount" error={meta.error} />
          </WSList>
        </WSCard>

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Back",
              kind: "Secondary",
              onClick: props.onBack,
              visible: !!props.onBack,
              type: "button"
            },
            {
              label: "Continue",
              loading: meta.isLoading
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
